import { useNavigate } from 'react-router-dom';
import { Toast } from '../../ui/toast';
import { STEPS } from '../../../constants/space-wizard.constants';
import { useConfigWizard } from '../../../hooks/useConfigWizard';
import { WizardSidebar } from './components/WizardSidebar';
import { WizardHeader } from './components/WizardHeader';
import { WizardSuccess } from './components/WizardSuccess';
import { WizardStepContent } from './components/WizardStepContent';
import { WizardPublishing } from './components/WizardPublishing';

interface ConfigWizardProps {
  initialData: any;
  onStepSave: (data: any, dirtyKeys: Set<string>) => Promise<void>;
  onFinish: (data: any) => Promise<void>;
}

export function ConfigWizard({ initialData, onStepSave, onFinish }: ConfigWizardProps) {
  const navigate = useNavigate();
  const {
    currentStep,
    formData,
    completedSteps,
    isSaving,
    isPublishing,
    saveError,
    toast,
    isWizardFinished,
    setToast,
    updateFormData,
    handleNext,
    handlePrevious,
    handleSkip,
    handleStepClick,
    handleSaveCurrentStep,
    handlePublish,
  } = useConfigWizard({
    initialData,
    onStepSave,
    onFinish,
    stepsCount: STEPS.length,
  });

  if (isPublishing) {
    return <WizardPublishing spaceName={formData.name} />;
  }

  if (isWizardFinished) {
    return (
      <WizardSuccess
        spaceName={formData.name}
        spaceSlug={formData.slug}
        isPublic={formData.isPublic}
        onGoBack={() => navigate('/spaces')}
      />
    );
  }

  const currentStepConfig = STEPS[currentStep];
  const StepComponent = currentStepConfig.component;

  return (
    <>
      <div style={{ display: 'flex', gap: '2rem', minHeight: 'calc(100vh - 200px)' }}>
        <WizardSidebar
          steps={STEPS}
          currentStep={currentStep}
          completedSteps={completedSteps}
          onStepClick={handleStepClick}
        />

        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <div
            style={{
              background: 'white',
              borderRadius: '16px',
              boxShadow: '0 4px 24px rgba(0,48,60,0.08)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              minHeight: '600px',
            }}
          >
            <WizardHeader
              label={currentStepConfig.label}
              description={currentStepConfig.description}
              isOptional={currentStepConfig.isOptional}
              currentStep={currentStep}
              totalSteps={STEPS.length}
              isSaving={isSaving}
              isValid={true} // TODO: Implement real validation
              onPrevious={handlePrevious}
              onSkip={handleSkip}
              onSave={handleSaveCurrentStep}
              onNext={handleNext}
            />

            <WizardStepContent saveError={saveError}>
              <StepComponent
                data={formData}
                onChange={updateFormData}
                onSave={handleSaveCurrentStep}
                isSaving={isSaving}
                onPublish={currentStepConfig.id === 'summary' ? handlePublish : undefined}
              />
            </WizardStepContent>
          </div>
        </div>

        {toast && (
          <Toast
            type={toast.type}
            message={toast.message}
            onClose={() => setToast(null)}
            duration={4000}
          />
        )}
      </div>
    </>
  );
}
